import type { RouteLocationRaw } from 'vue-router';
import router from '@/router';
import { useUserStore } from '@/stores/user.store';

/**
 * May the current user reach this route?
 *
 * The single answer to that question, used by the sidebar, the page-search
 * palette and every in-page link. The requirement is declared once — as
 * `meta.minRole` on the route — and this is what reads it, so a link can never
 * disagree with the guard that would refuse it, nor with another link to the
 * same target.
 *
 * Deliberately NOT a role check. `v-if="userStore.isAdmin"` on something that
 * navigates hardcodes an answer that belongs to the destination, which is how
 * nine files ended up offering pages the guard refuses, and how one link to the
 * ungated add-profile route ended up hiding itself for no reason.
 *
 * Role checks remain correct for elements that *do* something rather than go
 * somewhere; those have no destination to ask about.
 *
 * Takes anything `router.resolve` takes, so named targets and path strings both
 * work. Imports the router singleton rather than calling `useRouter`, so this is
 * callable outside `setup` — from a store action, or a plain helper.
 */
export const canVisit = (target: RouteLocationRaw): boolean => {
	const userStore = useUserStore();

	let resolved;

	try {
		resolved = router.resolve(target);
	} catch {
		// An unresolvable target hides the link rather than exposing it. A typo
		// should fail closed: the guard would refuse the navigation anyway.
		return false;
	}

	if (!resolved.matched.length) {
		return false;
	}

	const minRole = resolved.meta?.minRole as string | undefined;

	return !minRole || userStore.canAtLeast(minRole);
};
